/**
 * The optional water & mash-pH section of a recipe document (PLAN §5.4, §5.5).
 *
 * Field-for-field aligned with core's `RecipeWaterInput` so the bridge is a
 * mechanical `null` → omitted translation and nothing has to be recomputed on
 * the way in. Two shape differences, both deliberate:
 *
 * - salt and acid additions carry an `additionId` so the editor can address a
 *   row and `invert` can restore one precisely;
 * - `dilutionFraction`, `targetMashPh`, `acidifySparge` are **required** here
 *   (they always have a persisted value, defaulted at creation time) rather
 *   than optional-with-a-default as in core. A recipe's stored water settings
 *   should not silently change when core's defaults move.
 */

import { MAX_SULFATE_CHLORIDE_RATIO, MIN_SULFATE_CHLORIDE_RATIO } from '@beerwright/core';
import { z } from 'zod';
import { acidIdSchema, fractionSchema, nameSchema, percentSchema } from './common';
import { recipeAcidAdditionSchema, recipeSaltAdditionSchema } from './ingredients';

/**
 * The mash pH a new water section targets, pH units.
 *
 * Restated here rather than imported from core: a persisted document's stored
 * settings must not move when core's advisory default does.
 */
export const DEFAULT_DOC_TARGET_MASH_PH = 5.4;

/** ppm (mg/L) of one ion, bounded by what a water report can physically read. */
const ionPpmSchema = z.number().finite().min(0).max(1000);

/**
 * A flavour ion a report may simply not print: `null` means **not measured**.
 *
 * Not the same as `0`, which is a reading — RO water really does carry no
 * sulfate. The distinction only exists for the three ions that can go missing
 * without making the rest of the analysis useless; see
 * {@link waterProfileDocSchema}.
 */
const optionalIonPpmSchema = ionPpmSchema.nullable();

/**
 * A brewing water analysis, every ion in **ppm (mg/L)**.
 *
 * Alkalinity is stored as **bicarbonate** because that is an actual ion;
 * "alkalinity as CaCO₃" is a bookkeeping convention core converts at the edge.
 *
 * ## Three ions are nullable and three are not
 *
 * A water report is not a form the brewer failed to fill in; it is a document
 * that prints what the lab measured. Plenty of them — municipal annual reports
 * especially — print hardness and alkalinity and stop, and a brewer holding one
 * of those was previously obliged to type `0` into sulfate and chloride and
 * then be told, in confident language, what their water tasted like.
 *
 * So the **flavour ions** — sodium, chloride, sulfate — are nullable. They
 * decide how the beer reads, they are the ones reports omit, and a profile
 * without them still predicts mash pH perfectly well.
 *
 * The **pH ions** — calcium, magnesium, bicarbonate — are not. Every one of
 * them is load-bearing for residual alkalinity and the mash-pH prediction, and
 * a profile missing one is not a partial analysis but an unusable one. Refusing
 * it at the schema is better than carrying a hole through the engine and
 * printing a pH nobody should trust.
 */
export const waterProfileDocSchema = z.strictObject({
  /** Display name, e.g. `Burton on Trent`, or `null` for an unnamed report. */
  name: nameSchema.nullable(),
  /** Ca²⁺, ppm. */
  calciumPpm: ionPpmSchema,
  /** Mg²⁺, ppm. */
  magnesiumPpm: ionPpmSchema,
  /** Na⁺, ppm, or `null` when the report does not print it. */
  sodiumPpm: optionalIonPpmSchema,
  /** Cl⁻, ppm, or `null` when the report does not print it. */
  chloridePpm: optionalIonPpmSchema,
  /** SO₄²⁻, ppm, or `null` when the report does not print it. */
  sulfatePpm: optionalIonPpmSchema,
  /** HCO₃⁻, ppm. Alkalinity as CaCO₃ = this × 0.820162. */
  bicarbonatePpm: ionPpmSchema,
  /** Measured pH of the water as drawn, or `null` for core's assumption (7.5). */
  ph: z.number().finite().min(3).max(11).nullable(),
});

/** The ions a report may omit while the profile stays usable. */
export const OPTIONAL_DOC_IONS = ['sodiumPpm', 'chloridePpm', 'sulfatePpm'] as const;

/** @see OPTIONAL_DOC_IONS */
export type OptionalDocIon = (typeof OPTIONAL_DOC_IONS)[number];

/** @see waterProfileDocSchema */
export type WaterProfileDoc = z.infer<typeof waterProfileDocSchema>;

/** Ion-free RO/distilled water — the dilution default and a useful blank. */
export const RO_WATER_DOC: WaterProfileDoc = {
  name: 'RO / distilled',
  calciumPpm: 0,
  magnesiumPpm: 0,
  sodiumPpm: 0,
  chloridePpm: 0,
  sulfatePpm: 0,
  bicarbonatePpm: 0,
  ph: null,
};

/**
 * A target said in one breath: a calcium floor and a sulfate-to-chloride
 * balance (PLAN-WATER-RULES D-WR1).
 *
 * The other kind of target on this section is a {@link waterProfileDocSchema},
 * six numbers to be fitted as closely as the salts allow. This one is two
 * conditions to be *satisfied* — "at least this much calcium, and that
 * balance" — which is what a brewer actually says, and what core's
 * `solveWaterRule` can hit exactly rather than approach.
 *
 * The ratio's bounds are core's own {@link MIN_SULFATE_CHLORIDE_RATIO} and
 * {@link MAX_SULFATE_CHLORIDE_RATIO} rather than numbers restated here — unlike
 * {@link DEFAULT_DOC_TARGET_MASH_PH}, which is a stored *setting* whose value
 * must not move under a saved recipe. These are the range the solver can fit
 * at all, so a document holding a ratio outside them holds a target nothing
 * can answer; if core ever widens the range, a wider aim becomes storable, and
 * that is the right coupling.
 */
export const waterRuleDocSchema = z.strictObject({
  /** Calcium the treated water must reach, **ppm**. A floor, not an aim. */
  minCalciumPpm: z.number().finite().min(0).max(500),
  /** Sulfate ÷ chloride the treated water must land on. Bounds are core's. */
  sulfateChlorideRatio: z
    .number()
    .finite()
    .min(MIN_SULFATE_CHLORIDE_RATIO)
    .max(MAX_SULFATE_CHLORIDE_RATIO),
});

/** @see waterRuleDocSchema */
export type WaterRuleDoc = z.infer<typeof waterRuleDocSchema>;

/**
 * How many salt additions one water section may hold.
 *
 * Exported because an importer has to stop routing salts somewhere, and
 * `@beerwright/interchange`'s `build.ts` restated the figure to know where
 * (PLAN-UX-PASS C4). A cap a reader can reach and a cap a writer must respect
 * are one number.
 */
export const MAX_SALT_ADDITIONS = 32;

/** The water & mash-pH section of a recipe, before {@link migrateRecipeWater}. */
export const recipeWaterFieldsSchema = z.strictObject({
  /** The water as it comes out of the tap or the bottle. */
  sourceProfile: waterProfileDocSchema,
  /** Share of the brewing water replaced with RO/distilled, 0–1. */
  dilutionFraction: fractionSchema,
  /** What the dilution water is; `null` means ion-free RO. */
  dilutionProfile: waterProfileDocSchema.nullable(),
  /** Sparge water source when it differs from the mash source; `null` means "the same". */
  spargeSourceProfile: waterProfileDocSchema.nullable(),
  /** Salts added. */
  saltAdditions: z.array(recipeSaltAdditionSchema).max(MAX_SALT_ADDITIONS),
  /** Acids added. */
  acidAdditions: z.array(recipeAcidAdditionSchema).max(32),
  /** A target profile to report the achieved water against; purely informational. */
  targetProfile: waterProfileDocSchema.nullable(),
  /**
   * A target said as a rule instead of a profile, or `null`.
   *
   * At most one of this and `targetProfile` above is non-null — the section
   * aims at a profile or at a rule, never both — which {@link recipeWaterSchema}
   * enforces and the two setters maintain.
   *
   * `.default(null)` so a recipe written before rules existed still parses,
   * which is what keeps `RECIPE_SCHEMA_VERSION` from moving (see `version.ts`,
   * entry **3**). That is the opposite of what `kettleTargetPh` below does, and
   * the difference is not a change of heart: the rule against `.default()` on
   * this document guards against a default surviving `.partial()` and
   * materialising on a patch that never mentioned the key. **The water section
   * has no patch schema.** Every `water.*` command sets a whole value, so there
   * is no `.partial()` for this default to leak through, and the cheaper
   * addition is the honest one.
   */
  targetRule: waterRuleDocSchema.nullable().default(null),
  /** Target mash pH used for advice and warnings. */
  targetMashPh: z.number().finite().min(4).max(7),
  /** Acidify the sparge water. */
  acidifySparge: z.boolean(),
  /** Target sparge pH, or `null` for core's default (5.5). */
  spargeTargetPh: z.number().finite().min(4).max(7).nullable(),
  /** Which acid doses the sparge, or `null` for lactic. */
  spargeAcid: acidIdSchema.nullable(),
  /** Sparge acid strength, **% w/w**, or `null` for the acid's own default. */
  spargeAcidConcentrationPercent: percentSchema.nullable(),
  /**
   * Kettle pH the brew day aims at, or `null` for core's default (5.1).
   *
   * The one pH target on this document that no dose is predicted from. Mash and
   * sparge doses are solved here, from the recipe; a kettle dose is solved on
   * brew day from a meter reading, because the wort that reaches the boil is
   * not something a recipe can predict the pH of. See `kettlePh.ts` in core.
   *
   * `.nullable()` with **no `.default()`**, the rule this document model runs on
   * — a defaulted key survives `.partial()` and would materialise on every patch
   * that omits it. {@link migrateRecipeWater} fills the `null` instead.
   */
  kettleTargetPh: z.number().finite().min(4).max(7).nullable(),
});

/**
 * Read a water section saved before the kettle target existed.
 *
 * `null` is exactly what the field means for every one of them: no kettle
 * target was chosen, so core's default applies. Idempotent — a section that
 * already carries the key is returned untouched — which is what makes this safe
 * to run on every read, the same shape `migrateEquipmentProfileDoc` uses.
 */
export function migrateRecipeWater(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  if ('kettleTargetPh' in rec) return raw;
  return { ...rec, kettleTargetPh: null };
}

/**
 * The issue a section aiming at both targets reports, pointed at the newer key.
 *
 * Annotated rather than `as const` for the reason `ONE_PROVENANCE_ISSUE` in
 * `common.ts` is: Zod's `.refine()` takes `path?: PropertyKey[]`, and a
 * `readonly` tuple is not assignable to a mutable array.
 */
export const ONE_WATER_TARGET_ISSUE: { readonly message: string; readonly path: PropertyKey[] } = {
  message: 'A water section aims at a profile or a rule, not both.',
  path: ['targetRule'],
};

/**
 * The water & mash-pH section of a recipe. `null` on a recipe means "not using it".
 *
 * The one-of check lives out here, on the *preprocessed* schema, rather than on
 * {@link recipeWaterFieldsSchema}: Zod 4 refuses `.omit()` and `.partial()` on a
 * refined object, and while nothing reaches into the water section that way
 * today, putting the refinement on the fields object would quietly make it
 * impossible tomorrow. Refining the outside costs nothing and closes nothing off.
 */
export const recipeWaterSchema = z
  .preprocess(migrateRecipeWater, recipeWaterFieldsSchema)
  .refine(
    (water) => water.targetProfile === null || water.targetRule === null,
    ONE_WATER_TARGET_ISSUE,
  );

/** @see recipeWaterSchema */
export type RecipeWater = z.infer<typeof recipeWaterFieldsSchema>;

/**
 * A fresh water section around a source profile.
 *
 * Pure: the defaults are the constants below, never read from core at runtime,
 * so a document created today keeps its numbers if core's defaults move.
 */
export function blankRecipeWater(sourceProfile: WaterProfileDoc): RecipeWater {
  return {
    sourceProfile,
    dilutionFraction: 0,
    dilutionProfile: null,
    spargeSourceProfile: null,
    saltAdditions: [],
    acidAdditions: [],
    targetProfile: null,
    targetRule: null,
    targetMashPh: DEFAULT_DOC_TARGET_MASH_PH,
    acidifySparge: false,
    spargeTargetPh: null,
    spargeAcid: null,
    spargeAcidConcentrationPercent: null,
    kettleTargetPh: null,
  };
}
